import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import useForage from 'contexts/ForageContext/useForage';

export const EBT_BALANCE_QUERY_KEY = 'ebtBalance';

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

/**
 * Custom hook for EBT balance checking with 15-minute cache.
 * Provides methods to check EBT card balance and manage loading states.
 *
 * @param {string} last4 - The last 4 digits of the EBT card for cache key identification
 * @returns {Object} Hook return object containing balance data and methods
 * @returns {Object|null} returns.balance - Cached balance data with snap and cash amounts
 * @returns {Error|null} returns.balanceCheckError - Error from the last balance check attempt
 * @returns {boolean} returns.isBalanceLoading - Loading state for balance check operations
 * @returns {Function} returns.balanceCheck - Function to perform balance check
 * @returns {Function} returns.setBalanceCache - Function to manually set balance cache data
 */
function useEBTBalanceCheck(last4) {
    const { forage } = useForage();
    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.setQueryDefaults([EBT_BALANCE_QUERY_KEY], {
            gcTime: FIFTEEN_MINUTES_MS,
            staleTime: FIFTEEN_MINUTES_MS,
        });
    }, [queryClient]);

    function setBalanceCache(data) {
        queryClient.invalidateQueries({
            queryKey: [EBT_BALANCE_QUERY_KEY],
        });
        queryClient.setQueryData([EBT_BALANCE_QUERY_KEY, last4], data);
    }

    const mutation = useMutation({
        mutationFn: async ({ paymentMethodRef, pinElement }) => {
            if (!forage || !pinElement) {
                throw new Error('Missing required data for balance check');
            }

            const result = await forage.checkBalance(pinElement, paymentMethodRef);
            const { ebt, updated } = result;

            return {
                snap: Number(ebt?.snap) || 0,
                cash: Number(ebt?.cash) || 0,
                updated,
            };
        },
        onSuccess: (data) => {
            setBalanceCache(data);
            return data;
        },
    });

    const balanceCheck = async (pinElement, paymentMethodRef, onSuccess) => {
        const result = await mutation.mutateAsync({ pinElement, paymentMethodRef });
        onSuccess?.(result);
        return result;
    };

    return {
        balance: queryClient.getQueryData([EBT_BALANCE_QUERY_KEY, last4]),
        balanceCheckError: mutation.error,
        isBalanceLoading: mutation.isPending,
        balanceCheck,
        setBalanceCache,
    };
}

export default useEBTBalanceCheck;
